import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, LucideIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useLocation } from 'react-router-dom';

interface NavItem {
  id: string;
  title: string;
  icon: LucideIcon;
  path: string;
  badgeCount?: number;
  badgeColor?: string;
  isHighlighted?: boolean;
  hideForAdminOnly?: boolean;
  hideForGuided?: boolean;
  showOnlyForGuided?: boolean;
  requiresEmr?: boolean;
  disabledForTrial?: boolean;
  trialDisabledTooltip?: string;
  nimbusOnly?: boolean;
}

interface CollapsibleNavGroupProps {
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
  isCollapsed: boolean;
  defaultExpanded?: boolean;
  renderItem: (item: NavItem) => React.ReactNode;
}

const CollapsibleNavGroup: React.FC<CollapsibleNavGroupProps> = ({
  id,
  label,
  icon: Icon,
  items,
  isCollapsed,
  defaultExpanded = false,
  renderItem,
}) => {
  const location = useLocation();
  const storageKey = `sidebar-groups-${id}`;

  const getStoredExpanded = () => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored !== null ? stored === 'true' : defaultExpanded;
    } catch {
      return defaultExpanded;
    }
  };

  const [isExpanded, setIsExpanded] = useState(getStoredExpanded);

  // Auto-expand if an item in this group is active
  useEffect(() => {
    const hasActiveItem = items.some(item => {
      const cleanPath = item.path.split('?')[0];
      if (cleanPath === '/') return location.pathname === '/';
      return location.pathname.startsWith(cleanPath);
    });
    if (hasActiveItem && !isExpanded) {
      setIsExpanded(true);
    }
  }, [location.pathname, items]);

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    try {
      localStorage.setItem(storageKey, String(next));
    } catch {
      // ignore
    }
  };

  // Collapsed sidebar: show icon with tooltip showing group name
  if (isCollapsed) {
    return (
      <div className="relative group/collgroup">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center rounded-md p-2 cursor-pointer text-sidebar-foreground/50 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors">
              <Icon className="h-4 w-4" strokeWidth={1.8} />
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-slate-900 text-white border-slate-800">
            {label}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div>
      {/* Group header with toggle */}
      <button
        onClick={handleToggle}
        className="flex w-full items-center justify-between px-3 py-2 text-left transition-colors hover:bg-sidebar-accent/30 rounded-md group/btn"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-sidebar-foreground/40" strokeWidth={2} />
          <span className="text-[11px] font-semibold tracking-wide text-sidebar-foreground/40 group-hover/btn:text-sidebar-foreground/60 transition-colors">
            {label}
          </span>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 90 : 0 }}
          transition={{ duration: 0.15 }}
        >
          <ChevronRight className="h-3 w-3 text-sidebar-foreground/30 group-hover/btn:text-sidebar-foreground/50 transition-colors" />
        </motion.div>
      </button>

      {/* Collapsible items */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <nav className="space-y-0.5 px-2 pb-1">
              {items.map(item => renderItem(item))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CollapsibleNavGroup;
